import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Slack,
  Mail,
  Save,
  Trash2,
  Plus,
  ExternalLink,
  CheckCircle2,
  Loader2,
  ChevronLeft,
  Info,
} from "lucide-react";
import toast from "react-hot-toast";
import { axiosInstance } from "../lib/axios";
import { useAuthStore } from "../store/useAuthStore";

const IntegrationsSettingsPage = () => {
  const { authUser, checkAuth } = useAuthStore();
  const [slackUrl, setSlackUrl] = useState("");
  const [emails, setEmails] = useState<string[]>([]);
  const [newEmail, setNewEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    if (authUser) {
      setSlackUrl(authUser.slackWebhookUrl || "");
      setEmails(authUser.reportRecipientEmails || []);
    }
  }, [authUser]);

  const addEmail = () => {
    const trimmed = newEmail.trim();
    if (!trimmed) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      toast.error("Invalid email format");
      return;
    }
    if (emails.includes(trimmed)) {
      toast.error("Already added");
      return;
    }
    setEmails([...emails, trimmed]);
    setNewEmail("");
  };

  const removeEmail = (e: string) => {
    setEmails(emails.filter((x) => x !== e));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axiosInstance.put("/auth/update-integrations", {
        slackWebhookUrl: slackUrl.trim(),
        reportRecipientEmails: emails,
      });
      toast.success("Integration settings saved");
      await checkAuth();
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message || "Failed to save settings"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleTestSlack = async () => {
    if (!slackUrl.trim()) {
      toast.error("Add a Slack webhook URL first and save");
      return;
    }
    setTesting(true);
    try {
      await axiosInstance.post("/daily-reports/end-shift", {
        notes: "(Test message — disregard)",
      });
      toast.success(
        "Test report generated — check your Slack channel & inbox"
      );
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Test failed");
    } finally {
      setTesting(false);
    }
  };

  const slackValid =
    slackUrl.trim() === "" ||
    /^https:\/\/hooks\.slack\.com\/services\//.test(slackUrl.trim());

  return (
    <div className="min-h-screen bg-[#f8f9ff] pt-20 px-4 lg:px-8 pb-10">
      <div className="max-w-3xl mx-auto">
        <div className="mb-6">
          <Link
            to="/reports/daily"
            className="inline-flex items-center gap-1 text-[13px] font-semibold text-slate-600 hover:text-slate-900 mb-3"
          >
            <ChevronLeft size={14} />
            Daily Reports
          </Link>
          <h1 className="text-[26px] font-bold text-slate-900 tracking-tight">
            Integrations & Notifications
          </h1>
          <p className="text-[13px] text-slate-500 mt-1">
            Configure where your daily reports get delivered when you end your
            shift.
          </p>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-[0_4px_6px_-1px_rgba(0,0,0,0.05)] p-6 mb-5">
          <div className="flex items-start gap-3 mb-5">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-100 to-violet-200/60 grid place-items-center text-violet-700">
              <Slack size={18} />
            </div>
            <div className="flex-1">
              <h2 className="text-[16px] font-bold text-slate-900">
                Slack Webhook
              </h2>
              <p className="text-[12px] text-slate-500 mt-0.5">
                Each daily report will be posted to your channel via this
                Incoming Webhook URL.
              </p>
            </div>
            {authUser?.slackWebhookUrl && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold bg-emerald-50 text-emerald-700">
                <CheckCircle2 size={11} />
                Connected
              </span>
            )}
          </div>

          <label className="block text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-1.5">
            Webhook URL
          </label>
          <input
            type="text"
            value={slackUrl}
            onChange={(e) => setSlackUrl(e.target.value)}
            placeholder="https://hooks.slack.com/services/T.../B.../..."
            className={`w-full px-3 py-2.5 text-[13px] bg-slate-50 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              slackValid ? "border-slate-200" : "border-rose-300"
            }`}
          />
          {!slackValid && (
            <p className="text-[11px] text-rose-600 mt-1">
              Must start with https://hooks.slack.com/services/
            </p>
          )}
          <a
            href="https://api.slack.com/messaging/webhooks"
            target="_blank"
            rel="noreferrer"
            className="text-[11px] text-blue-600 hover:text-blue-800 inline-flex items-center gap-1 mt-2"
          >
            <Info size={11} />
            How to create a Slack Incoming Webhook
            <ExternalLink size={10} />
          </a>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-[0_4px_6px_-1px_rgba(0,0,0,0.05)] p-6 mb-5">
          <div className="flex items-start gap-3 mb-5">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-100 to-blue-200/60 grid place-items-center text-blue-700">
              <Mail size={18} />
            </div>
            <div className="flex-1">
              <h2 className="text-[16px] font-bold text-slate-900">
                Email Recipients
              </h2>
              <p className="text-[12px] text-slate-500 mt-0.5">
                Your own email is added automatically. Add managers or team
                leads who should receive your daily report.
              </p>
            </div>
          </div>

          <div className="flex gap-2 mb-3">
            <input
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addEmail())}
              placeholder="manager@company.com"
              className="flex-1 px-3 py-2.5 text-[13px] bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              onClick={addEmail}
              className="px-4 py-2 bg-slate-900 text-white rounded-md text-[12px] font-semibold inline-flex items-center gap-1"
            >
              <Plus size={14} />
              Add
            </button>
          </div>

          {emails.length === 0 ? (
            <p className="text-[12px] text-slate-400 italic">
              No additional recipients. You will still receive the report on
              your own email.
            </p>
          ) : (
            <div className="space-y-2">
              {emails.map((e) => (
                <div
                  key={e}
                  className="flex items-center justify-between p-2.5 bg-slate-50 rounded-md text-[13px]"
                >
                  <span className="text-slate-700 font-medium truncate">
                    {e}
                  </span>
                  <button
                    onClick={() => removeEmail(e)}
                    className="p-1 text-slate-400 hover:text-rose-600"
                    aria-label={`Remove ${e}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center justify-end gap-2">
          <button
            onClick={handleTestSlack}
            disabled={testing || !authUser?.slackWebhookUrl}
            className="px-4 py-2 text-[12px] font-semibold border border-slate-200 rounded-md hover:bg-slate-50 disabled:opacity-60 inline-flex items-center gap-1.5"
            title={
              authUser?.slackWebhookUrl
                ? "Generates a test report and sends it"
                : "Save a webhook first"
            }
          >
            {testing ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <Slack size={14} />
            )}
            Send Test Report
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !slackValid}
            className="px-4 py-2 text-[12px] font-semibold bg-[#0058c3] hover:opacity-90 text-white rounded-md inline-flex items-center gap-1.5 disabled:opacity-60"
          >
            {saving ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <Save size={14} />
            )}
            Save Settings
          </button>
        </div>

        <div className="mt-6 bg-blue-50 border border-blue-100 rounded-xl p-4 text-[12px] text-blue-900">
          <p className="font-bold mb-1 inline-flex items-center gap-1">
            <Info size={14} />
            Quick setup
          </p>
          <ol className="list-decimal ml-4 space-y-1 text-blue-800">
            <li>
              Open{" "}
              <a
                href="https://api.slack.com/apps"
                target="_blank"
                rel="noreferrer"
                className="underline"
              >
                api.slack.com/apps
              </a>{" "}
              → Create New App → From scratch.
            </li>
            <li>Pick your workspace, name it (e.g. "CRE Reports").</li>
            <li>Enable Incoming Webhooks → Add New Webhook to Workspace.</li>
            <li>Pick the channel (or your DM) → copy the webhook URL.</li>
            <li>Paste above → Save → Send Test Report.</li>
          </ol>
        </div>
      </div>
    </div>
  );
};

export default IntegrationsSettingsPage;
